/**
 * CopyVerificationDialog — v1.0.10 Items 2-8.
 *
 * Staff-only productivity action on the Employment/Landlord workspaces:
 * copies the completed verification result to one or more ACTIVE co-applicants
 * in the same household. Pure data-entry — never emails, never touches AAL,
 * Final Decision, DecisionPoint outcome, membership or ordered products.
 * Backend independently enforces same-household + active-member targeting.
 */
import { useEffect, useState } from "react";
import { Copy, AlertTriangle } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const TRACK_LABEL = { employment: "Employment", landlord: "Landlord" };

export default function CopyVerificationDialog({ sid, track, sourceName, onCopied }) {
  const [targets, setTargets] = useState(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [mode, setMode] = useState("fill_blanks");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    apiClient.get(`/screenings/${sid}/verifications/${track}/copy-targets`)
      .then(({ data }) => { if (live) setTargets(data.targets || []); })
      .catch(() => { if (live) setTargets([]); });
    return () => { live = false; };
  }, [sid, track, open]);

  if (!targets || targets.length === 0) return null;

  const chosen = targets.filter((t) => selected[t.screening_id]);
  const chosenWithData = chosen.filter((t) => t.has_existing);

  const doCopy = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/screenings/${sid}/verifications/${track}/copy`, {
        target_sids: chosen.map((t) => t.screening_id), mode,
      });
      toast.success(`Verification copied to ${data.copied.length} applicant${data.copied.length === 1 ? "" : "s"}`);
      setOpen(false);
      setSelected({});
      onCopied?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Copy failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="flex justify-end">
        <Button variant="outline" onClick={() => setOpen(true)} className="rounded-sm h-9"
                data-testid={`open-copy-verification-${track}`}>
          <Copy className="h-3.5 w-3.5 mr-2" /> Copy Verification to Co-Applicant
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Copy {TRACK_LABEL[track]} Verification
            </DialogTitle>
            <DialogDescription>
              Copy this verification from <strong>{sourceName}</strong> to active household members.
              Applicant identity is never copied — only the verified {track === "landlord" ? "tenancy" : "employment"} result.
            </DialogDescription>
          </DialogHeader>

          {track === "employment" && (
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-3"
                 data-testid="employment-copy-warning">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              Employment information may be applicant-specific. Review the copied verification
              before completing the destination applicant&rsquo;s verification.
            </div>
          )}

          <div className="space-y-2">
            <div className="ntn-eyebrow">Copy to</div>
            {targets.map((t) => (
              <label key={t.screening_id}
                     className="flex items-center gap-3 border border-slate-200 rounded-sm px-3 py-2.5 cursor-pointer hover:border-slate-400 transition-colors"
                     data-testid={`copy-target-${t.screening_id}`}>
                <Checkbox checked={!!selected[t.screening_id]}
                          onCheckedChange={(v) => setSelected((s) => ({ ...s, [t.screening_id]: !!v }))}
                          data-testid={`copy-target-check-${t.screening_id}`} />
                <span className="text-sm font-medium text-slate-900">{t.applicant_name}</span>
                <span className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500">
                  {(t.role || "").replace("_", " ")}
                </span>
                {t.has_existing && (
                  <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.1em] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                        data-testid={`copy-target-has-data-${t.screening_id}`}>
                    Has existing data
                  </span>
                )}
              </label>
            ))}
          </div>

          {chosenWithData.length > 0 && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-3"
                 data-testid="copy-existing-data-warning">
              <strong>{chosenWithData.map((t) => t.applicant_name).join(", ")}</strong>{" "}
              already {chosenWithData.length === 1 ? "contains" : "contain"} verification data.
              Choose how to handle existing information below — nothing is discarded silently.
            </div>
          )}

          <RadioGroup value={mode} onValueChange={setMode} className="space-y-1.5">
            <div className="flex items-center gap-2">
              <RadioGroupItem value="fill_blanks" id="mode-fill" data-testid="copy-mode-fill-blanks" />
              <Label htmlFor="mode-fill" className="text-sm">Copy only into blank fields (recommended)</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="replace" id="mode-replace" data-testid="copy-mode-replace" />
              <Label htmlFor="mode-replace" className="text-sm">Replace existing verification information</Label>
            </div>
          </RadioGroup>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm"
                    data-testid="copy-cancel">Cancel</Button>
            <Button onClick={doCopy} disabled={busy || chosen.length === 0}
                    className="bg-[#0A192F] rounded-sm" data-testid="copy-confirm">
              {busy ? "Copying…" : `Copy to ${chosen.length || "…"} applicant${chosen.length === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
